import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { useAntiSpam } from '../hooks/use-antispam';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { antiSpamFields, AntiSpamFields } = useAntiSpam();

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (email && !isSubmitting) {
      setIsSubmitting(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, ...antiSpamFields })
        });
        const data = await response.json();
        if (response.ok) {
          toast({
            title: "Thank you for subscribing!",
            description: "You'll receive our latest updates and impact stories."
          });
          setEmail('');
        } else {
          toast({
            title: "Subscription failed",
            description: data.detail || "Please try again.",
            variant: "destructive"
          });
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Network error. Please try again later.",
          variant: "destructive"
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Newsletter Section */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-white">
              <h3 className="text-2xl font-bold mb-2">Stay Connected</h3>
              <p className="text-orange-100">Get the latest updates on our mission to end hunger</p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2 w-full md:w-auto">
              <AntiSpamFields />
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white text-gray-900 placeholder:text-gray-500 border-0 w-full md:w-80"
                required />

              <Button type="submit" className="bg-gray-900 hover:bg-gray-800 text-white whitespace-nowrap" disabled={isSubmitting}>
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* About */}
          <div>
            <h4 className="text-white font-bold text-lg mb-4">About Us</h4>
            <ul className="space-y-2">
              <li><Link to="/who-we-are" className="hover:text-orange-500 transition-colors">Who We Are</Link></li>
              <li><Link to="/who-we-are#mission" className="hover:text-orange-500 transition-colors">Mission & Vision</Link></li>
              <li><Link to="/who-we-are#story" className="hover:text-orange-500 transition-colors">Our Story</Link></li>
              <li><Link to="/who-we-are#leadership" className="hover:text-orange-500 transition-colors">Leadership Team</Link></li>
              <li><Link to="/who-we-are#partners" className="hover:text-orange-500 transition-colors">Partners</Link></li>
            </ul>
          </div>

          {/* Our Work */}
          <div>
            <h4 className="text-white font-bold text-lg mb-4">Our Work</h4>
            <ul className="space-y-2">
              <li><Link to="/our-work" className="hover:text-orange-500 transition-colors">Our Programs</Link></li>
              <li><Link to="/meal-packaging" className="hover:text-orange-500 transition-colors">Meal Packaging</Link></li>
              <li><Link to="/our-work#where-we-work" className="hover:text-orange-500 transition-colors">Where We Work</Link></li>
              <li><Link to="/who-we-are#transparency" className="hover:text-orange-500 transition-colors">Financial Reports</Link></li>
            </ul>
          </div>

          {/* Get Involved */}
          <div>
            <h4 className="text-white font-bold text-lg mb-4">Get Involved</h4>
            <ul className="space-y-2">
              <li><Link to="/join-the-movement" className="hover:text-orange-500 transition-colors">Volunteer</Link></li>
              <li><Link to="/donate" className="hover:text-orange-500 transition-colors">Donate</Link></li>
              <li><Link to="/join-the-movement#corporate" className="hover:text-orange-500 transition-colors">Corporate Partnership</Link></li>
              <li><Link to="/join-the-movement#fundraiser" className="hover:text-orange-500 transition-colors">Start a Fundraiser</Link></li>
              <li><Link to="/join-the-movement#events" className="hover:text-orange-500 transition-colors">Events & Campaigns</Link></li>
            </ul>
          </div>

          {/* Terms & Policies */}
          <div>
            <h4 className="text-white font-bold text-lg mb-4">Terms & Policies</h4>
            <ul className="space-y-2">
              <li><Link to="/privacy-policy" className="hover:text-orange-500 transition-colors">Privacy Policy</Link></li>
              <li><Link to="/donor-terms" className="hover:text-orange-500 transition-colors">Donor Terms of Service</Link></li>
              <li><Link to="/refund-policy" className="hover:text-orange-500 transition-colors">Refund Policy</Link></li>
            </ul>
          </div>

          {/* International Locations */}
          <div>
            <h4 className="text-white font-bold text-lg mb-4">International Locations</h4>
            <ul className="space-y-2">
              <li><a href="https://italy.riseagainsthunger.org/" target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition-colors">Italy</a></li>
              <li><a href="https://malaysia.riseagainsthunger.org/" target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition-colors">Malaysia</a></li>
              <li><a href="https://philippines.riseagainsthunger.org/" target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition-colors">Philippines</a></li>
              <li><a href="https://singapore.riseagainsthunger.org/" target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition-colors">Singapore</a></li>
              <li><a href="https://africa.riseagainsthunger.org/" target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition-colors">South Africa</a></li>
              <li><a href="http://www.riseagainsthunger.org/" target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition-colors">United States</a></li>
            </ul>
          </div>
        </div>

        {/* Contact Info & Social */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Contact */}
            <div>
              <h4 className="text-white font-bold text-lg mb-4">Contact Us</h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-orange-500 flex-shrink-0 mt-1" />
                  <p className="text-sm">979, 3rd Cross, HRBR Layout 1st Block, Kalyan Nagar, Bangalore – 560043</p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={20} className="text-orange-500 flex-shrink-0" />
                  <p className="text-sm">+91 9108403329, +91 80 2542 6948</p>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={20} className="text-orange-500 flex-shrink-0" />
                  <a href="mailto:info@riseagainsthungerindia.org" className="text-sm hover:text-orange-500 transition-colors">
                    info@riseagainsthungerindia.org
                  </a>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <h4 className="text-white font-bold text-lg mb-4">Connect With Us</h4>
              <div className="flex gap-4">
                <a href="https://facebook.com/RiseAgainstHungerIndia" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Facebook"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors">
                  <Facebook size={20} />
                </a>
                <a href="https://x.com/RiseIndia2030" target="_blank" rel="noopener noreferrer" aria-label="Follow us on X (Twitter)"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors">
                  <Twitter size={20} />
                </a>
                <a href="https://www.instagram.com/riseagainsthungerindia/" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Instagram"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors">
                  <Instagram size={20} />
                </a>
                <a href="https://in.linkedin.com/company/rise-against-hunger-india" target="_blank" rel="noopener noreferrer" aria-label="Connect with us on LinkedIn"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors">
                  <Linkedin size={20} />
                </a>
                <a href="https://www.youtube.com/channel/UCaSIpDg2zU2MDLFBN3pMgEw" target="_blank" rel="noopener noreferrer" aria-label="Subscribe to our YouTube channel"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors">
                  <Youtube size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center space-x-3">
            {/* Desktop - Full Logo */}
            <img
              src="https://customer-assets.emergentagent.com/job_meal-mission-6/artifacts/k9pkqnwr_RAHlogo_india-01.jpg"
              alt="Rise Against Hunger India"
              className="h-12 w-auto bg-white p-1 rounded hidden sm:block"
              loading="lazy" />

            {/* Mobile - Monk Icon */}
            <img
              src="https://customer-assets.emergentagent.com/job_meal-mission-6/artifacts/o2lkxltt_MonkRAHI1.png"
              alt="Rise Against Hunger India"
              className="h-10 w-auto bg-white p-1 rounded sm:hidden"
              loading="lazy" />

            <p className="text-sm">© 2025 Rise Against Hunger India. All rights reserved.</p>
          </div>
          <p className="text-sm text-gray-400">Registered Charitable Trust | 80G & FCRA Certified</p>
        </div>
      </div>
    </footer>);

};

export default Footer;